import React, { useState, useRef, useEffect, useCallback } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';

import { Adg4RaisedMedia } from '@/components/adg4/raised-media/src/raised-media';
import { Picture } from '@/components/wpl/image/src/image';
import { wacToken } from '@/libraries/design/foundations';

import { ArrowButton, PlayPauseButton } from './carouselButtons.tsx';

export interface Slide {
	primaryText: string;
	description: string;
	imgSrc: string;
	imgAltText: string;
}

interface CarouselSlideProps extends Slide {
	id: string;
	stylingType?: StylingType;
	usePlainContainer?: boolean;
}

const carouselSlideStyle = css({
	display: 'flex',
	flexDirection: 'column',
	gap: '16px',
});

const imageStyle = css({ width: '100%' });

const infoContainerStyle = css({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	marginInline: '24px',
	gap: '16px',
	minHeight: '100px',
});

const textContainerStyle = css({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
});

const primaryTextStyle = css({ fontWeight: '600' });

const subTextStyle = css({ fontWeight: '400' });

const anchorPrimaryTextStyle = css({
	textAlign: 'center',
	color: wacToken('color.primary.black'),
	fontFamily: wacToken('font.charlie.display'),
	fontSize: '1.5rem',
	fontWeight: '700',
	lineHeight: '1.875rem',
	marginBlockEnd: '5px',
});

const anchorSubTextStyle = css({
	textAlign: 'center',
	color: wacToken('color.primary.black'),
	fontSize: '1rem',
	lineHeight: '1.5rem',
});

const plainImageContainer = css({
	display: 'flex',
	overflow: 'hidden',
});

export const CarouselSlide: React.FC<CarouselSlideProps> = ({
	primaryText,
	description,
	imgSrc,
	imgAltText,
	id,
	stylingType,
	usePlainContainer,
}) => {
	return (
		<div role="tabpanel" id={id} css={carouselSlideStyle}>
			{stylingType === 'anchor' ? (
				<Picture
					css={[imageStyle]}
					image={{
						src: imgSrc,
						alt: '',
					}}
				/>
			) : usePlainContainer ? (
				<div css={plainImageContainer}>
					{/* eslint-disable-next-line no-restricted-syntax */}
					<img src={imgSrc} alt={imgAltText} css={imageStyle} />
				</div>
			) : (
				<Adg4RaisedMedia>
					{/* eslint-disable-next-line no-restricted-syntax */}
					<img src={imgSrc} alt={imgAltText} css={imageStyle} />
				</Adg4RaisedMedia>
			)}
			<div css={infoContainerStyle}>
				<div css={textContainerStyle}>
					<span css={[primaryTextStyle, stylingType === 'anchor' && anchorPrimaryTextStyle]}>
						{primaryText}
					</span>
					<span css={[subTextStyle, stylingType === 'anchor' && anchorSubTextStyle]}>
						{description}
					</span>
				</div>
			</div>
		</div>
	);
};

const carouselTabListStyle = css({
	display: 'flex',
	gap: '24px',
});

const dotStyle = css({
	borderRadius: '50%',
	cursor: 'pointer',
	width: '12px',
	height: '12px',
	backgroundColor: wacToken('color.neutral.light0'),
});

const currentDotStyle = css({
	// eslint-disable-next-line local/ensure-wac-token-usage
	backgroundColor: '#0052CC',
});

const carouselStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
});

const controlsContainerStyles = css({
	display: 'grid',
	gridTemplateAreas: '". center right"',
	gridTemplateColumns: '1fr auto 1fr',
	gap: 'min(56px, 10%)',
});

const centeredControlsContainerStyles = css({
	gridArea: 'center',
	display: 'flex',
	alignItems: 'center',
	gap: '16px',
});

const rightAlignedControlsContainerStyles = css({
	gridArea: 'right',
});

type StylingType = 'anchor' | undefined;

export interface CarouselProps {
	'aria-label': string;
	slides: Array<Slide>;
	id: string;
	showPlayButton?: boolean;
	stylingType?: StylingType;
	usePlainContainer?: boolean;
}

export const Carousel: React.FC<CarouselProps> = ({
	'aria-label': ariaLabel,
	slides,
	id: carouselId,
	showPlayButton = true,
	stylingType,
	usePlainContainer,
}) => {
	const intl = useIntl();
	const cycleTime = 7000;
	const timeoutRef = useRef<null | number>(null);
	const tabsRef = useRef<Record<number, HTMLDivElement>>({});
	const [visibleSlideIndex, _setVisibleSlideIndex] = useState(0);
	const visibleSlide = slides[visibleSlideIndex];
	if (visibleSlide === undefined) {
		throw new Error('Slide index out of bounds.');
	}
	const setVisibleSlideIndex = (newState: React.SetStateAction<number>, moveFocus = false) => {
		_setVisibleSlideIndex((prevState) => {
			let calculatedState;
			if (typeof newState === 'function') {
				calculatedState = newState(prevState);
			} else {
				calculatedState = newState;
			}
			moveFocus && tabsRef?.current[calculatedState]?.focus();
			return calculatedState;
		});
	};

	const moveToPreviousSlide = (moveFocus = false) => {
		setVisibleSlideIndex((prev) => {
			return prev === 0 ? slides.length - 1 : prev - 1;
		}, moveFocus);
	};

	const moveToNextSlide = useCallback(
		(moveFocus = false) => {
			setVisibleSlideIndex((prev) => {
				return prev === slides.length - 1 ? 0 : prev + 1;
			}, moveFocus);
		},
		[slides.length],
	);

	const [isRotating, setIsRotating] = useState(true);

	const stopRotating = () => {
		timeoutRef?.current !== null && window.clearInterval(timeoutRef.current);
		setIsRotating(false);
	};

	const startRotating = () => {
		timeoutRef.current = window.setInterval(() => {
			moveToNextSlide();
		}, cycleTime);
		setIsRotating(true);
	};

	const toggleRotating = () => {
		isRotating ? stopRotating() : startRotating();
	};

	useEffect(startRotating, [moveToNextSlide]);

	const handleKeyDown = (e: React.KeyboardEvent) => {
		switch (e.key) {
			case 'ArrowRight':
				moveToNextSlide(true);
				break;
			case 'ArrowLeft':
				moveToPreviousSlide(true);
				break;
			case 'Home':
				setVisibleSlideIndex(0, true);
				break;
			case 'End':
				setVisibleSlideIndex(slides.length - 1, true);
				break;
			default:
				return;
		}
		stopRotating();
		e.preventDefault();
	};

	const handleTabClick = (index: number) => {
		stopRotating();
		setVisibleSlideIndex(index);
	};

	const handlePreviousClick = () => {
		stopRotating();
		moveToPreviousSlide();
	};

	const handleNextClick = () => {
		stopRotating();
		moveToNextSlide();
	};

	const getSlideId = (suffix: string | number) => `${carouselId}-${suffix}`;

	return (
		<section aria-label={ariaLabel} aria-roledescription="carousel" css={carouselStyles}>
			<div aria-atomic={false} aria-live={isRotating ? 'off' : 'polite'}>
				<CarouselSlide
					key={visibleSlide.primaryText}
					{...visibleSlide}
					id={getSlideId(visibleSlideIndex)}
					stylingType={stylingType}
					usePlainContainer={usePlainContainer}
				/>
			</div>
			<div css={controlsContainerStyles}>
				<div css={centeredControlsContainerStyles}>
					<ArrowButton
						onClick={handlePreviousClick}
						icon={<ChevronLeftIcon label="" />}
						aria-label={intl.formatMessage({
							id: 'confluence.paid.lp.carousel.previous.label',
							defaultMessage: 'Previous slide',
						})}
					/>
					<div
						role="tablist"
						aria-label={intl.formatMessage({
							id: 'confluence.paid.lp.carousel.tablist.label',
							defaultMessage: 'Choose a slide to display.',
						})}
						css={carouselTabListStyle}
					>
						{slides.map((slide, index) => {
							const isVisibleTab = visibleSlideIndex === index;
							return (
								<div
									aria-controls={getSlideId(index)}
									aria-selected={isVisibleTab}
									key={`${slide.primaryText}-${index}`}
									role="tab"
									aria-label={intl.formatMessage(
										{
											id: 'confluence.paid.lp.carousel.tab.label',
											defaultMessage: `Slide {slideNumber}`,
										},
										{ slideNumber: `${index + 1}` },
									)}
									tabIndex={isVisibleTab ? 0 : -1}
									onKeyDown={handleKeyDown}
									onClick={() => handleTabClick(index)}
									css={[dotStyle, isVisibleTab && currentDotStyle]}
									ref={(node) => {
										if (node) {
											tabsRef.current[index] = node;
										}
									}}
								/>
							);
						})}
					</div>
					<ArrowButton
						onClick={handleNextClick}
						aria-label={intl.formatMessage({
							id: 'confluence.paid.lp.carousel.next.label',
							defaultMessage: 'Next slide',
						})}
						icon={<ChevronRightIcon label="" />}
					/>
				</div>
				{showPlayButton && (
					<div css={rightAlignedControlsContainerStyles}>
						<PlayPauseButton onClick={toggleRotating} isRotating={isRotating} />
					</div>
				)}
			</div>
		</section>
	);
};
